import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SquareTile from "../generic/tiles/SquareTile";
import { LoadingSpinner, Button } from "../generic";
import { useAuth } from "src/context/AuthContext";
import { stripe as stripeUtils } from "src/utils";
import BillingInner from "./BillingInner";
import type { SubscriptionSummary } from "src/utils/stripe/subscription";;

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_ACCESS_KEY || "");

const Billing: FunctionComponent = () => {
  const { user } = useAuth();
  const customerId = user?.metadata?.stripeId as string | undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [paymentMethodLast4, setPaymentMethodLast4] = useState<string | null>(null);

  const elementsOptions = useMemo(() => {
    if (!clientSecret) return undefined;
    return { clientSecret };
  }, [clientSecret]);

  const refresh = async () => {
    if (!customerId) return;

    setError("");
    setLoading(true);
    try {
      // 1) Subscription snapshot
      const sub = await stripeUtils.subscription.fetchSubscription(customerId);
      setSubscription(sub);

      // 2) Payment method (optional display)
      const pm = await stripeUtils.payments.fetchCustomerPaymentMethod(customerId);
      // adjust to your API response shape:
      setPaymentMethodLast4(pm?.card?.last4 ?? pm?.paymentMethod?.card?.last4 ?? null);

      // 3) SetupIntent for (new card / subscription / upgrade)
      const si = await stripeUtils.payments.createSetupIntentForSubscription(customerId);
      // adjust to your API response shape:
      const secret = si?.clientSecret || si?.client_secret;
      if (!secret) throw new Error("Missing client secret from SetupIntent");
      setClientSecret(secret);
    } catch (e: any) {
      setError(e?.message || "Failed to load billing details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (!user) return <LoadingSpinner />;

  return (
    <SquareTile>
      {loading && <LoadingSpinner />}
      {!loading && error && <p className="text-red-600">{error}</p>}

      {!loading && !error && !customerId && (
        <div className="space-y-3">
          <p>Missing Stripe customer id for this user.</p>
          <p className="text-sm opacity-80">
            Ensure <code>user.metadata.stripeId</code> is set after onboarding.
          </p>
        </div>
      )}

      {!loading && !error && customerId && clientSecret && elementsOptions && (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <BillingInner
            customerId={customerId}
            subscription={subscription}
            paymentMethodLast4={paymentMethodLast4}
            clientSecret={clientSecret}
            accountType={user?.userType}
            onChanged={refresh}
          />
        </Elements>
      )}

      {!loading && !error && customerId && !clientSecret && (
        <div className="space-y-3">
          <p>Unable to initialize payment form.</p>
          <Button text="Retry" onClick={refresh} />
        </div>
      )}
    </SquareTile>
  );
};

export default Billing;
